import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import DownloadButton from '@/components/Elements/Button/DownloadButton'
import ViewFileButton from '@/components/Elements/Button/ViewFileButton'
import { Button, Flex, HStack } from '@chakra-ui/react'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

import useFile from '@/features/files/useFile'
import { allFileTypes } from '@/utils/constants'
import { getFileExtension } from '@/utils/fileHelper'
import FieldWrapper from './FieldWrapper'

const FileInput = ({
  name,
  fileType,
  accept = allFileTypes,
  label,
  canUpload,
  disabled = false,
  isRow = false,
  isGuest = false,
  defaultFile = null,
  extraOnUpload = () => {},
  ...rest
}) => {
  const [file, setFile] = useState(null)
  const [showButtonGroup, setShowButtonGroup] = useState(false)
  const [showUploadField, setShowUploadField] = useState(false)
  const [showNoUploadText, setShowNoUploadText] = useState(false)

  const { register, setValue } = useFormContext()
  const field = useWatch({ name })
  const { useRemoveFile, useUploadFile, useFetchFile } = useFile()
  const removeFile = useRemoveFile()
  const uploadFile = useUploadFile()

  const dispatch = useDispatch()

  useEffect(() => {
    register(name)

    if (defaultFile) {
      setFile(defaultFile)
      setValue(name, defaultFile, { shouldDirty: true })
    }
  }, [register, name, defaultFile])

  useEffect(() => {
    if (field && field?._id && field?.originalFileName) {
      setFile(field)
    } else {
      setFile(null)
    }
  }, [field])

  useEffect(() => {
    if (file) {
      setShowButtonGroup(true)
      setShowUploadField(false)
      setShowNoUploadText(false)
    } else {
      setShowButtonGroup(false)
      setShowUploadField(canUpload)
      setShowNoUploadText(!canUpload)
    }
  }, [file, canUpload])

  const fileChangedHandler = async (e) => {
    if (!fileType) {
      console.error('File type is required')
      return
    }

    if (e.target.files.length === 0) {
      console.error('No file selected')
      return
    }

    try {
      const res = await uploadFile.mutateAsync({ version: 0, type: fileType, files: e.target.files, isGuest: isGuest })
      setFile(res.data[0])
      setValue(name, res.data[0], { shouldDirty: true })

      if (typeof extraOnUpload === 'function') {
        extraOnUpload(e)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleRemoveFile = (fileId) => {
    removeFile.mutate(fileId, {
      onSuccess: () => {
        setFile(null)
        setValue(name, null, { shouldDirty: true })
      },
    })
  }

  return (
    <FieldWrapper name={name} label={label} isRow={isRow} {...rest}>
      <Flex>
        {showButtonGroup && (
          <HStack>
            {getFileExtension(file?.originalFileName) === 'pdf' && <ViewFileButton fileId={file?._id} />}

            <DownloadButton fileId={file?._id} fileName={file?.originalFileName} />
            {!disabled && (
              <Button colorScheme="red" onClick={() => handleRemoveFile(file._id)}>
                Remove
              </Button>
            )}
          </HStack>
        )}

        {showUploadField && <input type="file" accept={accept} onChange={fileChangedHandler} />}

        {showNoUploadText && <span>(No File Uploaded)</span>}
      </Flex>
    </FieldWrapper>
  )
}

export default FileInput
